/**
 * Product page membership upsell functionality
 * Handles checkbox selection and adds membership to cart with main product
 */

// Prevent multiple event listeners
if (window.productMembershipUpsellLoaded) {
  console.log("Product membership upsell script already loaded");
} else {
  window.productMembershipUpsellLoaded = true;

  console.log("Loading product membership upsell functionality");

  // Flag to prevent double processing
  let isProcessingMembershipAdd = false;

  // Intercept product form submissions with capture phase to run before other handlers
  document.addEventListener("submit", async (event) => {
    const form = event.target;
    
    // Only handle product forms
    if (!form.matches('[action*="/cart/add"]') && !form.id.includes('product-form')) {
      return;
    }

    console.log("Product form submission detected:", form);

    // Check if membership checkbox is checked
    const membershipCheckbox = document.querySelector('#membership-upsell-checkbox');
    if (!membershipCheckbox || !membershipCheckbox.checked) {
      console.log("Membership not selected, proceeding with normal add to cart");
      return; // Let normal form submission continue
    }

    // Prevent double processing
    if (isProcessingMembershipAdd) {
      console.log("Already processing membership add, preventing duplicate");
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    console.log("Membership selected, intercepting form submission");
    
    // Set processing flag
    isProcessingMembershipAdd = true;
    
    // Prevent ALL form submission behavior
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Temporarily disable the form to prevent any other submissions
    const originalAction = form.action;
    form.action = "javascript:void(0)";
    
    // Get the submit button to show loading state
    const submitButton = form.querySelector('[type="submit"]');
    const originalButtonText = submitButton ? submitButton.textContent : '';
    
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Adding...';
    }

    // Get main product data
    const formData = new FormData(form);
    const mainProductId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;
    
    console.log("Main product data:", { mainProductId, quantity });
    console.log("All form data:", Object.fromEntries(formData));

    // Get membership data
    const membershipVariantId = parseInt(membershipCheckbox.dataset.membershipVariant);
    const membershipSellingPlanId = parseInt(membershipCheckbox.dataset.membershipSellingPlan);
    
    console.log("Membership data:", { membershipVariantId, membershipSellingPlanId });

    if (!mainProductId || !membershipVariantId) {
      console.error("Missing required product IDs");
      alert("Error: Missing product information");
      // Reset form action
      form.action = originalAction;
      isProcessingMembershipAdd = false;
      return;
    }

    try {
      // Check if membership is already in cart
      const currentCart = await fetch("/cart.js").then((r) => r.json());
      const membershipExists = currentCart.items.find((item) => item.variant_id === membershipVariantId);
      
      // Prepare items to add
      const itemsToAdd = [
        {
          id: mainProductId,
          quantity: quantity,
        }
      ];

      // Only add membership if not already in cart
      if (!membershipExists) {
        const membershipItem = {
          id: membershipVariantId,
          quantity: 1,
        };
        
        if (!isNaN(membershipSellingPlanId)) {
          membershipItem.selling_plan = membershipSellingPlanId;
        }
        
        itemsToAdd.push(membershipItem);
        console.log("Adding membership to cart");
      } else {
        console.log("Membership already in cart, only adding main product");
      }

      console.log("Items to add to cart:", itemsToAdd);

      // Add all items to cart
      const addResponse = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items: itemsToAdd }),
      });

      if (!addResponse.ok) {
        const errorText = await addResponse.text();
        console.error("Cart add response error:", errorText);
        throw new Error("Failed to add items to cart");
      }

      const addResult = await addResponse.json();
      console.log("Add to cart response:", addResult);

      console.log("Items added successfully");

      if (submitButton) {
        submitButton.textContent = 'Added!';
      }

      // Fetch updated cart content to trigger proper cart refresh
      const updatedCartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cartContent = await updatedCartResponse.json();
      
      // Dispatch variant added event for main product (following theme pattern)
      const mainProductVariant = addResult.items ? addResult.items[0] : addResult;
      if (mainProductVariant) {
        form.dispatchEvent(new CustomEvent("variant:added", {
          bubbles: true,
          detail: {
            variant: mainProductVariant
          }
        }));
      }
      
      // Dispatch cart updated event
      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
        bubbles: true,
        detail: {
          cart: cartContent
        }
      }));

      // Trigger cart refresh events with mini cart open flag (following theme pattern)
      const shouldOpenMiniCart = (window.themeVariables?.settings?.cartType === "drawer" || 
                                  !window.themeVariables?.settings?.cartType) && // Default to drawer if not set
                                 form.closest(".drawer") === null; // Don't open drawer within drawer

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart: cartContent,
          openMiniCart: shouldOpenMiniCart
        }
      }));

      console.log("Cart refresh event dispatched with openMiniCart:", shouldOpenMiniCart);

      // Update mini cart if present (fallback)
      setTimeout(() => {
        const miniCart = document.querySelector('#mini-cart');
        if (miniCart && typeof miniCart._rerenderCart === 'function') {
          console.log("Calling _rerenderCart method as fallback");
          miniCart._rerenderCart({});
        }
      }, 100);

      // Show success notification (following theme pattern)
      form.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: "success",
          error: ""
        }
      }));

      // Show success message or redirect based on theme behavior
      setTimeout(() => {
        // Check if theme has a success action (like opening cart drawer)
        const event = new CustomEvent('product:added-to-cart', {
          bubbles: true,
          detail: { 
            items: itemsToAdd,
            hasMembership: !membershipExists 
          }
        });
        document.dispatchEvent(event);
      }, 200);

    } catch (error) {
      console.error("Error adding items to cart:", error);
      
      // Show error notification (following theme pattern)
      form.dispatchEvent(new CustomEvent("cart-notification:show", {
        bubbles: true,
        cancelable: true,
        detail: {
          status: "error",
          error: error.message || "Failed to add items to cart"
        }
      }));
      
      alert("Failed to add items to cart. Please try again.");
    } finally {
      // Reset processing flag
      isProcessingMembershipAdd = false;
      
      // Restore form action
      form.action = originalAction;
      
      // Reset button state
      setTimeout(() => {
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText;
        }
      }, 2000);
    }
  }, true); // Use capture phase to run before other handlers

  // Handle checkbox state changes
  document.addEventListener("change", (event) => {
    if (event.target.id === 'membership-upsell-checkbox') {
      console.log("Membership checkbox changed:", event.target.checked);
      
      // Update UI to show what will be added
      const checkbox = event.target;
      const upsellContainer = checkbox.closest('.product-membership-upsell');
      
      if (checkbox.checked) {
        upsellContainer?.classList.add('selected');
        console.log("Membership upsell selected");
      } else {
        upsellContainer?.classList.remove('selected');
        console.log("Membership upsell deselected");
      }
    }
  });

  // Initialize checkbox state on page load
  document.addEventListener("DOMContentLoaded", () => {
    const checkbox = document.querySelector('#membership-upsell-checkbox');
    if (checkbox) {
      const upsellContainer = checkbox.closest('.product-membership-upsell');
      if (checkbox.checked) {
        upsellContainer?.classList.add('selected');
      }
    }
  });

} // End of productMembershipUpsellLoaded check